import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Users, FileText, ClipboardList, Loader2, X,
} from "lucide-react";

// ============================================================
// USER MANAGEMENT TAB
// ============================================================
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [allSchemas, setAllSchemas] = useState([]);
  const [allReports, setAllReports] = useState([]);
  const [form, setForm] = useState({
    email: "", password: "", role: "user", allowed_schemas: [], allowed_reports: [],
  });

  const loadData = useCallback(async () => {
    try {
      const [usersRes, schemasRes, reportsRes] = await Promise.all([
        api.get("/admin/users"),
        api.get("/allowed-schemas"),
        api.get("/admin/reports"),
      ]);
      setUsers(usersRes.data);
      setAllSchemas(schemasRes.data);
      setAllReports(reportsRes.data);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => {
    setEditingUser(null);
    setForm({ email: "", password: "", role: "user", allowed_schemas: [], allowed_reports: [] });
    setDialogOpen(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setForm({
      email: user.email,
      password: "",
      role: user.role,
      allowed_schemas: user.allowed_schemas || [],
      allowed_reports: user.allowed_reports || [],
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingUser) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/admin/users/${editingUser._id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/admin/users", form);
        toast.success("User created");
      }
      setDialogOpen(false);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save user");
    }
  };

  const toggleActive = async (user) => {
    try {
      await api.put(`/admin/users/${user._id}`, { active: !user.active });
      loadData();
    } catch {
      toast.error("Failed to update user");
    }
  };

  const toggleSchema = (schema) => {
    setForm((prev) => ({
      ...prev,
      allowed_schemas: prev.allowed_schemas.includes(schema)
        ? prev.allowed_schemas.filter((s) => s !== schema)
        : [...prev.allowed_schemas, schema],
    }));
  };

  const toggleReport = (key) => {
    setForm((prev) => ({
      ...prev,
      allowed_reports: prev.allowed_reports.includes(key)
        ? prev.allowed_reports.filter((r) => r !== key)
        : [...prev.allowed_reports, key],
    }));
  };

  if (loading) return <div className="text-[#A3A3A3] py-8">Loading users...</div>;

  return (
    <div data-testid="users-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white" style={{ fontFamily: "Chivo" }}>
          Users ({users.length})
        </h2>
        <Button
          onClick={openCreate}
          data-testid="create-user-button"
          className="bg-[#0066FF] hover:bg-[#005CE6] text-white"
          size="sm"
        >
          <Plus size={16} className="mr-2" /> Create User
        </Button>
      </div>

      <div className="bg-[#141414] border border-[#262626] rounded-md">
        <Table>
          <TableHeader>
            <TableRow className="border-[#262626] hover:bg-transparent">
              <TableHead className="text-xs uppercase text-[#A3A3A3]">Email</TableHead>
              <TableHead className="text-xs uppercase text-[#A3A3A3]">Role</TableHead>
              <TableHead className="text-xs uppercase text-[#A3A3A3]">Status</TableHead>
              <TableHead className="text-xs uppercase text-[#A3A3A3]">Schemas</TableHead>
              <TableHead className="text-xs uppercase text-[#A3A3A3] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user._id} className="border-[#262626] hover:bg-[#1E1E1E]">
                <TableCell className="text-sm text-white">{user.email}</TableCell>
                <TableCell>
                  <Badge variant={user.role === "admin" ? "default" : "secondary"} className="text-xs">
                    {user.role}
                  </Badge>
                </TableCell>
                <TableCell>
                  <button
                    onClick={() => toggleActive(user)}
                    data-testid={`toggle-active-${user._id}`}
                    className={`text-xs px-2 py-0.5 rounded cursor-pointer ${
                      user.active !== false
                        ? "bg-green-500/10 text-green-400 border border-green-500/20"
                        : "bg-red-500/10 text-red-400 border border-red-500/20"
                    }`}
                  >
                    {user.active !== false ? "Active" : "Inactive"}
                  </button>
                </TableCell>
                <TableCell className="text-sm text-[#A3A3A3]">
                  {(user.allowed_schemas || []).length || "All"}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEdit(user)}
                    data-testid={`edit-user-${user._id}`}
                    className="text-[#A3A3A3] hover:text-white"
                  >
                    <Pencil size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#141414] border-[#262626] max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: "Chivo" }}>
              {editingUser ? "Edit User" : "Create User"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase text-[#A3A3A3]">Email</Label>
              <Input
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                data-testid="user-form-email"
                className="bg-[#0A0A0A] border-[#262626] mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase text-[#A3A3A3]">
                Password {editingUser && "(leave blank to keep)"}
              </Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                data-testid="user-form-password"
                className="bg-[#0A0A0A] border-[#262626] mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase text-[#A3A3A3]">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm((p) => ({ ...p, role: v }))}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#262626] mt-1" data-testid="user-form-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase text-[#A3A3A3] mb-2 block">
                Allowed Schemas
              </Label>
              <div className="flex flex-wrap gap-2">
                {allSchemas.map((s) => (
                  <label
                    key={s}
                    className="flex items-center gap-1.5 text-sm text-white cursor-pointer"
                  >
                    <Checkbox
                      checked={form.allowed_schemas.includes(s)}
                      onCheckedChange={() => toggleSchema(s)}
                    />
                    {s}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase text-[#A3A3A3] mb-2 block">
                Allowed Reports (empty = all)
              </Label>
              <div className="flex flex-wrap gap-2">
                {allReports.map((r) => (
                  <label
                    key={r.key}
                    className="flex items-center gap-1.5 text-sm text-white cursor-pointer"
                  >
                    <Checkbox
                      checked={form.allowed_reports.includes(r.key)}
                      onCheckedChange={() => toggleReport(r.key)}
                    />
                    {r.name}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-[#262626] text-white">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              data-testid="user-form-save"
              className="bg-[#0066FF] hover:bg-[#005CE6] text-white"
            >
              {editingUser ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
// REPORT MANAGEMENT TAB
// ============================================================
function ReportsTab() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [form, setForm] = useState({
    key: "", name: "", description: "", category: "Custom",
    sql_text: "", filters: [], allowed_schemas: [],
    export_excel: true, export_csv: true, active: true,
  });

  const loadReports = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/reports");
      setReports(data);
    } catch {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadReports(); }, [loadReports]);

  const openCreate = () => {
    setEditingReport(null);
    setForm({
      key: "", name: "", description: "", category: "Custom",
      sql_text: "", filters: [], allowed_schemas: [],
      export_excel: true, export_csv: true, active: true,
    });
    setDialogOpen(true);
  };

  const openEdit = (report) => {
    setEditingReport(report);
    setForm({
      key: report.key, name: report.name, description: report.description || "",
      category: report.category, sql_text: report.sql_text,
      filters: report.filters || [], allowed_schemas: report.allowed_schemas || [],
      export_excel: report.export_excel, export_csv: report.export_csv,
      active: report.active,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingReport) {
        const { key, ...updateData } = form;
        await api.put(`/admin/reports/${editingReport.key}`, updateData);
        toast.success("Report updated");
      } else {
        await api.post("/admin/reports", form);
        toast.success("Report created");
      }
      setDialogOpen(false);
      loadReports();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save report");
    }
  };

  const handleDelete = async (key) => {
    if (!window.confirm("Delete this report?")) return;
    try {
      await api.delete(`/admin/reports/${key}`);
      toast.success("Report deleted");
      loadReports();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const addFilter = () => {
    setForm((prev) => ({
      ...prev,
      filters: [...prev.filters, { key: "", label: "", type: "text", required: false }],
    }));
  };

  const updateFilter = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      filters: prev.filters.map((f, i) =>
        i === index ? { ...f, [field]: value } : f
      ),
    }));
  };

  const removeFilter = (index) => {
    setForm((prev) => ({
      ...prev,
      filters: prev.filters.filter((_, i) => i !== index),
    }));
  };

  if (loading) return <div className="text-[#A3A3A3] py-8">Loading reports...</div>;

  return (
    <div data-testid="reports-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white" style={{ fontFamily: "Chivo" }}>
          Reports ({reports.length})
        </h2>
        <Button
          onClick={openCreate}
          data-testid="create-report-button"
          className="bg-[#0066FF] hover:bg-[#005CE6] text-white"
          size="sm"
        >
          <Plus size={16} className="mr-2" /> Create Report
        </Button>
      </div>

      <div className="bg-[#141414] border border-[#262626] rounded-md">
        <Table>
          <TableHeader>
            <TableRow className="border-[#262626] hover:bg-transparent">
              <TableHead className="text-xs uppercase text-[#A3A3A3]">Name</TableHead>
              <TableHead className="text-xs uppercase text-[#A3A3A3]">Key</TableHead>
              <TableHead className="text-xs uppercase text-[#A3A3A3]">Category</TableHead>
              <TableHead className="text-xs uppercase text-[#A3A3A3]">Type</TableHead>
              <TableHead className="text-xs uppercase text-[#A3A3A3]">Status</TableHead>
              <TableHead className="text-xs uppercase text-[#A3A3A3] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reports.map((r) => (
              <TableRow key={r.key} className="border-[#262626] hover:bg-[#1E1E1E]">
                <TableCell className="text-sm text-white">{r.name}</TableCell>
                <TableCell className="text-sm text-[#A3A3A3] font-mono">{r.key}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs border-[#262626] text-[#A3A3A3]">
                    {r.category}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge
                    variant={r.system_report ? "secondary" : "outline"}
                    className="text-xs"
                  >
                    {r.system_report ? "System" : "Custom"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <span className={`text-xs ${r.active ? "text-green-400" : "text-red-400"}`}>
                    {r.active ? "Active" : "Inactive"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  {!r.system_report && (
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => openEdit(r)}
                        data-testid={`edit-report-${r.key}`}
                        className="text-[#A3A3A3] hover:text-white"
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => handleDelete(r.key)}
                        data-testid={`delete-report-${r.key}`}
                        className="text-[#A3A3A3] hover:text-red-400"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#141414] border-[#262626] max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: "Chivo" }}>
              {editingReport ? "Edit Report" : "Create Report"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase text-[#A3A3A3]">Key</Label>
                <Input
                  value={form.key}
                  onChange={(e) => setForm((p) => ({ ...p, key: e.target.value }))}
                  disabled={!!editingReport}
                  data-testid="report-form-key"
                  className="bg-[#0A0A0A] border-[#262626] mt-1 font-mono"
                  placeholder="unique_report_key"
                />
              </div>
              <div>
                <Label className="text-xs uppercase text-[#A3A3A3]">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm((p) => ({ ...p, category: v }))}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#262626] mt-1" data-testid="report-form-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["Accounting", "Inventory", "Sales", "Custom"].map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase text-[#A3A3A3]">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                data-testid="report-form-name"
                className="bg-[#0A0A0A] border-[#262626] mt-1"
                placeholder="Report Name"
              />
            </div>
            <div>
              <Label className="text-xs uppercase text-[#A3A3A3]">Description</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                data-testid="report-form-description"
                className="bg-[#0A0A0A] border-[#262626] mt-1"
                placeholder="Brief description"
              />
            </div>
            <div>
              <Label className="text-xs uppercase text-[#A3A3A3]">SQL Query</Label>
              <textarea
                value={form.sql_text}
                onChange={(e) => setForm((p) => ({ ...p, sql_text: e.target.value }))}
                data-testid="report-form-sql"
                className="w-full h-40 mt-1 p-3 rounded-md bg-[#0A0A0A] border border-[#262626] text-sm text-white font-mono resize-y focus:border-[#0066FF] focus:outline-none focus:ring-1 focus:ring-[#0066FF]"
                placeholder='SELECT * FROM "TABLE" WHERE "Column" = ?'
              />
            </div>

            {/* Filters Builder */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase text-[#A3A3A3]">Filters</Label>
                <Button variant="outline" size="sm" onClick={addFilter} className="border-[#262626] text-white text-xs">
                  <Plus size={14} className="mr-1" /> Add Filter
                </Button>
              </div>
              {form.filters.map((filter, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_1fr_120px_60px_32px] gap-2 mb-2 items-center">
                  <Input
                    value={filter.key}
                    onChange={(e) => updateFilter(idx, "key", e.target.value)}
                    className="bg-[#0A0A0A] border-[#262626] text-sm"
                    placeholder="key"
                  />
                  <Input
                    value={filter.label}
                    onChange={(e) => updateFilter(idx, "label", e.target.value)}
                    className="bg-[#0A0A0A] border-[#262626] text-sm"
                    placeholder="Label"
                  />
                  <Select value={filter.type} onValueChange={(v) => updateFilter(idx, "type", v)}>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["text", "date_range", "date", "select", "number", "warehouse"].map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <label className="flex items-center gap-1 text-xs text-[#A3A3A3]">
                    <Checkbox
                      checked={filter.required}
                      onCheckedChange={(v) => updateFilter(idx, "required", v)}
                    />
                    Req
                  </label>
                  <Button variant="ghost" size="sm" onClick={() => removeFilter(idx)} className="text-[#A3A3A3] hover:text-red-400 p-0 h-8 w-8">
                    <X size={14} />
                  </Button>
                </div>
              ))}
            </div>

            {/* Export options */}
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm text-white">
                <Checkbox
                  checked={form.export_excel}
                  onCheckedChange={(v) => setForm((p) => ({ ...p, export_excel: v }))}
                />
                Excel Export
              </label>
              <label className="flex items-center gap-2 text-sm text-white">
                <Checkbox
                  checked={form.export_csv}
                  onCheckedChange={(v) => setForm((p) => ({ ...p, export_csv: v }))}
                />
                CSV Export
              </label>
              <label className="flex items-center gap-2 text-sm text-white">
                <Checkbox
                  checked={form.active}
                  onCheckedChange={(v) => setForm((p) => ({ ...p, active: v }))}
                />
                Active
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-[#262626] text-white">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              data-testid="report-form-save"
              className="bg-[#0066FF] hover:bg-[#005CE6] text-white"
            >
              {editingReport ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
// AUDIT LOGS TAB
// ============================================================
function AuditLogsTab() {
  const [logs, setLogs] = useState([]);
  const [inquiryLogs, setInquiryLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState("reports");

  useEffect(() => {
    const loadLogs = async () => {
      try {
        const [auditRes, inquiryRes] = await Promise.all([
          api.get("/admin/audit-logs?limit=200"),
          api.get("/admin/inquiry-logs?limit=200"),
        ]);
        setLogs(auditRes.data.logs);
        setInquiryLogs(inquiryRes.data.logs);
      } catch {
        toast.error("Failed to load logs");
      } finally {
        setLoading(false);
      }
    };
    loadLogs();
  }, []);

  if (loading) return <div className="text-[#A3A3A3] py-8">Loading logs...</div>;

  return (
    <div data-testid="audit-logs-tab">
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveSubTab("reports")}
          className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
            activeSubTab === "reports" ? "bg-[#1E1E1E] text-white" : "text-[#A3A3A3] hover:text-white"
          }`}
        >
          Report Runs ({logs.length})
        </button>
        <button
          onClick={() => setActiveSubTab("inquiries")}
          className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
            activeSubTab === "inquiries" ? "bg-[#1E1E1E] text-white" : "text-[#A3A3A3] hover:text-white"
          }`}
        >
          Product Inquiries ({inquiryLogs.length})
        </button>
      </div>

      {activeSubTab === "reports" && (
        <div className="bg-[#141414] border border-[#262626] rounded-md overflow-auto max-h-[600px]">
          <Table>
            <TableHeader>
              <TableRow className="border-[#262626] hover:bg-transparent">
                <TableHead className="text-xs uppercase text-[#A3A3A3] sticky top-0 bg-[#141414]">User</TableHead>
                <TableHead className="text-xs uppercase text-[#A3A3A3] sticky top-0 bg-[#141414]">Report</TableHead>
                <TableHead className="text-xs uppercase text-[#A3A3A3] sticky top-0 bg-[#141414]">Schema</TableHead>
                <TableHead className="text-xs uppercase text-[#A3A3A3] sticky top-0 bg-[#141414]">Rows</TableHead>
                <TableHead className="text-xs uppercase text-[#A3A3A3] sticky top-0 bg-[#141414]">Export</TableHead>
                <TableHead className="text-xs uppercase text-[#A3A3A3] sticky top-0 bg-[#141414]">Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log, i) => (
                <TableRow key={i} className="border-[#262626] hover:bg-[#1E1E1E]">
                  <TableCell className="text-sm text-white">{log.user_email}</TableCell>
                  <TableCell className="text-sm text-[#A3A3A3] font-mono">{log.report_key}</TableCell>
                  <TableCell className="text-sm text-[#A3A3A3]">{log.schema}</TableCell>
                  <TableCell className="text-sm text-white">{log.row_count}</TableCell>
                  <TableCell className="text-sm text-[#A3A3A3]">{log.export_format}</TableCell>
                  <TableCell className="text-sm text-[#A3A3A3]">
                    {log.created_at ? new Date(log.created_at).toLocaleString() : ""}
                  </TableCell>
                </TableRow>
              ))}
              {logs.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-[#A3A3A3] py-8">No logs yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {activeSubTab === "inquiries" && (
        <div className="bg-[#141414] border border-[#262626] rounded-md overflow-auto max-h-[600px]">
          <Table>
            <TableHeader>
              <TableRow className="border-[#262626] hover:bg-transparent">
                <TableHead className="text-xs uppercase text-[#A3A3A3] sticky top-0 bg-[#141414]">User</TableHead>
                <TableHead className="text-xs uppercase text-[#A3A3A3] sticky top-0 bg-[#141414]">Schema</TableHead>
                <TableHead className="text-xs uppercase text-[#A3A3A3] sticky top-0 bg-[#141414]">Search</TableHead>
                <TableHead className="text-xs uppercase text-[#A3A3A3] sticky top-0 bg-[#141414]">Warehouse</TableHead>
                <TableHead className="text-xs uppercase text-[#A3A3A3] sticky top-0 bg-[#141414]">Results</TableHead>
                <TableHead className="text-xs uppercase text-[#A3A3A3] sticky top-0 bg-[#141414]">Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inquiryLogs.map((log, i) => (
                <TableRow key={i} className="border-[#262626] hover:bg-[#1E1E1E]">
                  <TableCell className="text-sm text-white">{log.user_email}</TableCell>
                  <TableCell className="text-sm text-[#A3A3A3]">{log.schema}</TableCell>
                  <TableCell className="text-sm text-white">{log.search_term}</TableCell>
                  <TableCell className="text-sm text-[#A3A3A3]">{log.warehouse || "All"}</TableCell>
                  <TableCell className="text-sm text-white">{log.row_count}</TableCell>
                  <TableCell className="text-sm text-[#A3A3A3]">
                    {log.created_at ? new Date(log.created_at).toLocaleString() : ""}
                  </TableCell>
                </TableRow>
              ))}
              {inquiryLogs.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-[#A3A3A3] py-8">No logs yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// ADMIN PAGE
// ============================================================
export default function AdminPage() {
  return (
    <div data-testid="admin-page">
      <h1
        className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-6"
        style={{ fontFamily: "Chivo, sans-serif" }}
      >
        Admin Panel
      </h1>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="bg-[#141414] border border-[#262626] h-10">
          <TabsTrigger
            value="users"
            data-testid="admin-tab-users"
            className="data-[state=active]:bg-[#1E1E1E] data-[state=active]:text-white text-[#A3A3A3]"
          >
            <Users size={16} className="mr-2" /> Users
          </TabsTrigger>
          <TabsTrigger
            value="reports"
            data-testid="admin-tab-reports"
            className="data-[state=active]:bg-[#1E1E1E] data-[state=active]:text-white text-[#A3A3A3]"
          >
            <FileText size={16} className="mr-2" /> Reports
          </TabsTrigger>
          <TabsTrigger
            value="logs"
            data-testid="admin-tab-logs"
            className="data-[state=active]:bg-[#1E1E1E] data-[state=active]:text-white text-[#A3A3A3]"
          >
            <ClipboardList size={16} className="mr-2" /> Audit Logs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-6">
          <UsersTab />
        </TabsContent>
        <TabsContent value="reports" className="mt-6">
          <ReportsTab />
        </TabsContent>
        <TabsContent value="logs" className="mt-6">
          <AuditLogsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
